import AccessToken from 'domain/entity/session/models/AccessToken'
import RefreshToken from 'domain/entity/session/models/RefreshToken'
import OAuthRepository from 'domain/repository/oauth/OAuthRepository'
import SessionRepository from 'domain/repository/session/SessionRepository'

export default class TokenRefreshService {
	private refreshPromise: Promise<void> | undefined
	private onSessionExpiredCallback: (() => void) | undefined

	constructor(private readonly sessionRepository: SessionRepository) {}

	/** Регистрирует колбэк, который вызывается при истечении refresh token (invalid_grant). */
	public onSessionExpired(callback: () => void): void {
		this.onSessionExpiredCallback = callback
	}

	/** Возвращает валидный access token, при необходимости обновляя его.
	 *  Параллельные вызовы во время refresh ждут одного общего промиса. */
	public async getAccessToken(): Promise<AccessToken | null> {
		// Ждём завершения текущего refresh, если он идёт
		await this.refreshPromise

		const accessToken = this.sessionRepository.getAccessToken()
		if (!accessToken) {
			return null
		}

		// Не проверяем expiry на клиенте: часы сервера могут отличаться от клиентских.
		// Сервер сам вернёт ACCESS_TOKEN_EXPIRED если токен протух — interceptor
		// сделает refresh и повторит вызов.
		return accessToken
	}

	public getRefreshTokenRaw(): string | null {
		return this.sessionRepository.getRefreshTokenRaw()
	}

	/** Обновляет токены. Повторные вызовы во время выполнения получают тот же промис. */
	public refreshAccessToken(refreshTokenRaw: string): Promise<void> {
		return (
			this.refreshPromise ??
			(this.refreshPromise = (async () => {
				try {
					const { accessToken, refreshToken } = await new OAuthRepository().refreshToken(refreshTokenRaw)
					this.sessionRepository.saveAccessToken(new AccessToken(accessToken))
					this.sessionRepository.saveRefreshToken(new RefreshToken(refreshToken))
				} catch (error) {
					const isInvalidGrant =
						error instanceof Error && error.message.toLowerCase().includes('invalid_grant')
					if (isInvalidGrant) {
						this.sessionRepository.destroyToken()
						OAuthRepository.clearProvider()
						this.onSessionExpiredCallback?.()
					}
					throw error
				} finally {
					this.refreshPromise = undefined
				}
			})())
		)
	}
}
